/**
 * connection_id -> metadata, status, and the actions it can run.
 *
 * The public API for anything built on this platform: hold an id, get back
 * everything you need, never touch a credential.
 */

import { useQuery } from '@tanstack/react-query'
import { actionsApi } from '@/core/api/actions.api'
import { connectionsApi } from '@/core/api/connections.api'
import { queryKeys } from '@/lib/query.client'
import { useConnections } from './connection.context'
import type { ActionSummary, Connection } from '@/core/types/api.types'

export interface UseConnectionResult {
  connection: Connection | undefined
  actions: ActionSummary[]
  /** Actions worth showing in a runner: lookups exist to feed dropdowns. */
  runnableActions: ActionSummary[]
  isLoading: boolean
  error: unknown
}

export function useConnection(connectionId: string | null | undefined): UseConnectionResult {
  const { byId } = useConnections()

  const detail = useQuery({
    queryKey: queryKeys.availableActions(connectionId ?? ''),
    enabled: Boolean(connectionId),
    // Actions change only when the server's manifests change, i.e. on deploy.
    staleTime: 10 * 60_000,
    queryFn: () => actionsApi.available(connectionId!),
  })

  const actions = detail.data?.actions ?? []

  return {
    // The context already holds the list, so the common case needs no request.
    connection: connectionId ? byId[connectionId] : undefined,
    actions,
    runnableActions: actions.filter((a) => !a.is_lookup),
    isLoading: detail.isLoading,
    error: detail.error,
  }
}

/** On-demand health check. Not automatic: each call hits the provider. */
export function useConnectionHealth(connectionId: string | null | undefined, enabled = false) {
  return useQuery({
    queryKey: queryKeys.health(connectionId ?? ''),
    enabled: Boolean(connectionId) && enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: () => connectionsApi.health(connectionId!),
  })
}
